import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import ErrorBoundary from './components/ErrorBoundary';
import LandingPage from './pages/LandingPage';
import ModelsLanding from './ModelsLanding';
import OptimizeHub from './OptimizeHub';
import LayerOptimizer from './components/LayerOptimizer';
import SignInPage from './components/auth/SignInPage';

// Component to handle auth expiration globally
function AuthExpiredHandler() {
  const { signOut } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleAuthExpired = async (event) => {
      const message = event.detail?.message || 'Your session has expired. Please log in again.';

      // Show alert to user
      alert(message);

      // Sign out the user
      await signOut();

      // Navigate to sign in page
      navigate('/signin', { replace: true });
    };

    // Listen for auth:expired event
    window.addEventListener('auth:expired', handleAuthExpired);

    // Cleanup
    return () => {
      window.removeEventListener('auth:expired', handleAuthExpired);
    };
  }, [signOut, navigate]);

  return null; // This component doesn't render anything
}

// Route wrapper component for LayerOptimizer
function LayerOptimizerPage() {
  const { modelId, hardware, layerId } = useParams();
  const navigate = useNavigate();
  const { session, loading: authLoading } = useAuth();
  const [modelData, setModelData] = useState(null);
  const [modelLayers, setModelLayers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchModelData = async () => {
      // Don't fetch if auth is still loading or no session
      if (authLoading || !session?.access_token) {
        return;
      }

      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/api/models/${modelId}`, {
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setModelData(data);

          // Layers are now lightweight (no function_code or golden data)
          // Full layer details are fetched on-demand when needed
          // Filter out read-only layers for the code editor
          const editableLayers = data.layers.filter(layer => !layer.is_read_only);
          setModelLayers(editableLayers);
        } else {
          console.error('Failed to fetch model data');
        }
      } catch (error) {
        console.error('Error fetching model data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchModelData();
  }, [modelId, session, authLoading]);

  // Get the selected layer ID from URL params, or default to first layer
  const selectedLayerId = layerId || modelLayers[0]?.id;

  const handleClose = () => {
    // Navigate back to model detail page (hardware-agnostic)
    navigate(`/models/${modelId}`);
  };

  const handleSaveOptimization = async (optimizationData) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/api/models/layers/${optimizationData.layerId}/optimize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({
          layerId: optimizationData.layerId,
          layerName: optimizationData.layerName,
          code: optimizationData.code,
          estimatedSpeedup: parseFloat(optimizationData.estimatedSpeedup),
          technique: 'Manual Optimization',
          author: 'Developer',
          message: `Optimized layer ${optimizationData.layerName}: ${optimizationData.estimatedSpeedup}x speedup using manual optimization`
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Optimization saved successfully:', result);
        return result;
      } else {
        console.error('Failed to save optimization');
        throw new Error('Failed to save optimization');
      }
    } catch (error) {
      console.error('Error saving optimization:', error);
      throw error;
    }
  };

  if (authLoading || loading) {
    return (
      <div className="fixed inset-0 bg-gray-900 z-50 flex items-center justify-center">
        <div className="text-white">
          {authLoading ? 'Authenticating...' : 'Loading model data...'}
        </div>
      </div>
    );
  }

  if (!modelData || modelLayers.length === 0) {
    return (
      <div className="fixed inset-0 bg-gray-900 z-50 flex items-center justify-center">
        <div className="text-white">Model data not found</div>
      </div>
    );
  }

  return (
    <LayerOptimizer
      selectedLayerId={selectedLayerId}
      modelLayers={modelLayers}
      onClose={handleClose}
      onSaveOptimization={handleSaveOptimization}
      modelName={modelData.model?.name || modelData.name}
      modelId={modelId}
      hardware={hardware}
    />
  );
}

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <AuthExpiredHandler />
          <Routes>
            {/* Public routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/signin" element={<SignInPage />} />

            {/* Protected routes - require authentication */}
            <Route path="/explore" element={
              <ProtectedRoute>
                <ErrorBoundary>
                  <ModelsLanding />
                </ErrorBoundary>
              </ProtectedRoute>
            } />
            <Route path="/models" element={<Navigate to="/explore" replace />} />
            {/* Hardware-agnostic model route */}
            <Route path="/models/:modelId" element={
              <ProtectedRoute>
                <ErrorBoundary>
                  <OptimizeHub />
                </ErrorBoundary>
              </ProtectedRoute>
            } />
            {/* Hardware-specific model route */}
            <Route path="/models/:modelId/:hardware" element={
              <ProtectedRoute>
                <ErrorBoundary>
                  <OptimizeHub />
                </ErrorBoundary>
              </ProtectedRoute>
            } />
            {/* Hardware-agnostic optimizer route */}
            <Route path="/models/:modelId/optimize/:layerId" element={
              <ProtectedRoute>
                <ErrorBoundary>
                  <LayerOptimizerPage />
                </ErrorBoundary>
              </ProtectedRoute>
            } />
            {/* Hardware-specific optimizer route */}
            <Route path="/models/:modelId/:hardware/optimize/:layerId" element={
              <ProtectedRoute>
                <ErrorBoundary>
                  <LayerOptimizerPage />
                </ErrorBoundary>
              </ProtectedRoute>
            } />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
